import { Component, OnInit, Inject } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MonitoringService } from '../../monitoring.service';
import { CompanySearchPopupComponent } from '../company-search-popup/company-search-popup.component';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';

interface UploadData {
  name?: string;
  isin?: string;
  rowNumber: number;
  hasWarning?: boolean;
  warningMessage?: string;
}

interface MatchResult {
  input_data: { name?: string; isin?: string; };
  matched_entity_id?: string;
  matched_entity_name?: string;
  matched_isin?: string;
  match_score?: number;
  match_quality: 'high' | 'low' | 'no_match' | 'duplicate';
  gics_subindustry?: string;
  company_type?: string;
  selected?: boolean;
  isDuplicate?: boolean;
  isExisting?: boolean;
  isManual?: boolean;
  rowNumber: number;
}

@Component({
  selector: 'app-csv-upload',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatSnackBarModule, MatCardModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatTableModule, MatTooltipModule,
    MatCheckboxModule, MatTabsModule, MatDialogModule
  ],
  templateUrl: './csv-upload.component.html',
  styleUrls: ['./csv-upload.component.css']
})
export class CsvUploadComponent implements OnInit {
  selectedFile: File | null = null;
  uploadData: UploadData[] = [];
  matchResults: MatchResult[] = [];
  isProcessing = false;
  currentStep: 'upload' | 'review' = 'upload';
  progressValue = 0;
  progressInterval: any;
  Math = Math;

  displayedColumns = ['select', 'match_score', 'input_name', 'input_isin', 'matched_name', 'matched_isin', 'actions'];

  remainingSlots = 0;
  //if organisation is QuantIP set the limit to 500 else 200
  maxCompanies = localStorage.getItem('organisation') === 'QuantIP' ? 500 : 200;

  maxFileSize = 5 * 1024 * 1024; // 5MB
  maxRows = localStorage.getItem('organisation') === 'QuantIP' ? 500 : 200;
  minRows = 1;
  wasAutoSelected = false;
  existingCount = 0;
  activeTab: 'matched' | 'partial' = 'matched';
  existingCompanies: any[] = [];
  watchlistId: number | null = null;
  benchmarkId: number | null = null;
  isBenchmark: boolean = false;

  constructor(
    private monitoringService: MonitoringService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<CsvUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.isBenchmark = this.data?.isBenchmark || false;
    if (this.isBenchmark && this.data?.benchmark) {
      this.benchmarkId = this.data.benchmark.benchmark_id;
      this.existingCount = this.data.benchmark.company_count || 0;
      // Benchmarks have unlimited slots
      this.maxCompanies = Infinity;
      this.maxRows = Infinity;
      this.remainingSlots = Infinity;
    } else {
      this.watchlistId = this.data?.watchlist?.watchlist_id;
      this.existingCount = this.data?.watchlist?.company_count || 0;
      this.remainingSlots = this.maxCompanies - this.existingCount;
    }
    this.loadExistingCompanies();
  }


  searchSubject = new Subject<string>();

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(search => {
      this.searchText = search;
    })
  }
  ngOnDestroy(): void {
    this.stopProgressAnimation();
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }
  loadExistingCompanies(): void {
    if (!this.watchlistId) return;
    this.monitoringService.getWatchlistCompanies(this.watchlistId).subscribe({
      next: (res) => {
        this.existingCompanies = res.body?.payload || [];
        if (res.status === 204) {
          this.existingCompanies = [];
        }
      },
      error: () => {
        this.existingCompanies = [];
        this.snackBar.open('Failed to load existing companies', 'Close', { duration: 5000 });
      }
    });
  }



  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Clear previous data
      this.uploadData = [];
      this.matchResults = [];
      this.currentStep = 'upload';
      this.wasAutoSelected = false;

      // Check file size
      if (file.size > this.maxFileSize) {
        this.snackBar.open(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds 5MB limit. Please use a smaller file.`, 'Close', { duration: 8000 });
        return;
      }

      this.selectedFile = file;
      this.parseFile(file);
    }
    event.target.value = '';
    // console.log("On File Selected");
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    // console.log("On Drag Over");
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.uploadData = [];
      this.matchResults = [];
      this.currentStep = 'upload';
      this.selectedFile = files[0];
      this.parseFile(files[0]);
    }
    // console.log("On Drop");
  }

  parseFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        let workbook;
        if (file.name.toLowerCase().endsWith('.csv')) {
          const csvData = e.target.result;
          workbook = XLSX.read(csvData, { type: 'string' });
        } else {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: 'array' });
        }
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        if (!jsonData || jsonData.length === 0) {
          this.snackBar.open('No valid company data found in the file', 'Close', { duration: 5000 });
          return;
        }

        this.uploadData = [];
        const firstRow = jsonData[0] as any;
        const hasCompanyName = firstRow && (firstRow['Company Name'] !== undefined || firstRow['name'] !== undefined || firstRow['Name'] !== undefined);
        const hasISIN = firstRow && (firstRow['ISIN'] !== undefined || firstRow['isin'] !== undefined);

        if (!hasCompanyName && !hasISIN) {
          this.snackBar.open('File must contain either "Company Name" or "ISIN" column. Please download our template.', 'Close', { duration: 5000 });
          return;
        }
        const validExtensions = ['.csv', '.xlsx', '.xls'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!validExtensions.includes(fileExtension)) {
          this.snackBar.open('Unsupported file type. Please use CSV or Excel files.', 'Close', { duration: 5000 });
          return;
        }

        this.uploadData = jsonData.map((row: any, index: number) => {
          const companyName = row['Company Name'] || row['name'] || row['Name'];
          const isin = row['ISIN'] || row['isin'];
          const rowNumber = index + 2;

          let hasWarning = false;
          let warningMessage = '';

          if (!companyName && !isin) {
            hasWarning = true;
            warningMessage = 'Missing both Company Name and ISIN';
          } else if (!companyName) {
            hasWarning = true;
            warningMessage = 'Missing Company Name';
          } else if (!isin) {
            hasWarning = true;
            warningMessage = 'Missing ISIN';
          }

          return { name: companyName, isin: isin, rowNumber, hasWarning, warningMessage };
        }).filter(company => company.name || company.isin);

        if (this.uploadData.length === 0) {
          this.snackBar.open('No valid company data found in the file', 'Close', { duration: 5000 });
        } else if (this.uploadData.length < this.minRows) {
          this.snackBar.open(`File must contain at least ${this.minRows} company`, 'Close', { duration: 5000 });
        } else if (!this.isBenchmark && this.uploadData.length > this.maxRows) {
          // Auto-select first maxRows rows (only for watchlists, not benchmarks)
          this.uploadData = this.uploadData.slice(0, this.maxRows);
          this.wasAutoSelected = true;
          this.checkForDuplicates();
        } else if (!this.isBenchmark && this.uploadData.length > this.remainingSlots) {
          this.snackBar.open(`File contains ${this.uploadData.length} companies, but only ${this.remainingSlots} slots remaining (${this.existingCount}/${this.maxCompanies} used)`, 'Close', { duration: 5000 });
        } else {
          this.checkForDuplicates();
        }
      } catch (error) {
        this.snackBar.open('Error parsing file. Please check the format.', 'Close', { duration: 5000 });
        console.error('File parsing error:', error);
      }
    };

    // Handle both CSV and Excel files
    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }

  checkForDuplicates(): void {
    const isinCounts = new Map<string, number[]>();
    const rowCounts = new Map<string, number[]>();

    this.uploadData.forEach((company, index) => {
      if (company.isin) {
        if (!isinCounts.has(company.isin)) {
          isinCounts.set(company.isin, []);
        }
        isinCounts.get(company.isin)!.push(company.rowNumber);
      }

      const rowKey = `${company.name || ''}-${company.isin || ''}`;
      if (!rowCounts.has(rowKey)) {
        rowCounts.set(rowKey, []);
      }
      rowCounts.get(rowKey)!.push(company.rowNumber);
    });

    isinCounts.forEach((rows, isin) => {
      if (rows.length > 1) {
        rows.forEach(rowNum => {
          const company = this.uploadData.find(c => c.rowNumber === rowNum);
          if (company) {
            company.hasWarning = true;
            company.warningMessage = `Duplicate ISIN: ${isin}`;
          }
        });
      }
    });

    rowCounts.forEach((rows, key) => {
      if (rows.length > 1) {
        rows.forEach(rowNum => {
          const company = this.uploadData.find(c => c.rowNumber === rowNum);
          if (company && !company.hasWarning) {
            company.hasWarning = true;
            company.warningMessage = 'Duplicate row';
          }
        });
      }
    });
  }

  processFile(): void {
    // Processing of the uploaded file: Bulk match uploaded rows, then review and select unique valid companies
    if (!this.selectedFile || this.uploadData.length === 0) return;

    this.isProcessing = true;
    this.progressValue = 0;
    this.startProgressAnimation();

    const bulkMatchRequests = this.uploadData.map(company => ({ name: company.name, isin: company.isin }));

    this.monitoringService.bulkMatchCompanies(bulkMatchRequests).subscribe({
      next: (response) => {
        this.progressValue = 100;
        this.stopProgressAnimation();

        setTimeout(() => {
          this.isProcessing = false;
          this.matchResults = response.map((match, index) => ({
            ...match,
            selected: match.match_quality === 'high' && match.gics_subindustry && (match.company_type === 'Public Company' || match.company_type === 'Public Investment Firm'),
            isDuplicate: false,
            rowNumber: this.uploadData[index]?.rowNumber || index + 1
          }));

          this.checkForDuplicateMatches();
          this.checkExistingCompanies();
          this.currentStep = 'review';
          this.initTooltips();
          // Scroll dialog container to top when entering review step
          setTimeout(() => {
            const container = document.querySelector('.container-fluid.p-0') as HTMLElement;
            if (container) container.scrollTop = 0;
          }, 100);
          this.snackBar.open(`Found ${response.filter(r => r.match_quality === 'high').length} high-quality matches out of ${response.length} companies`, 'View Matches', { duration: 5000 });
        }, 500);
      },
      error: (error) => {
        this.isProcessing = false;
        this.stopProgressAnimation();
        this.snackBar.open('No matching companies found. Try removing ISIN values or check company names.', 'Close', { duration: 5000 });
        console.error('Matching error:', error);
      }
    });
  }

  initTooltips(): void {
    setTimeout(() => {
      const tooltipTriggerList = document.querySelectorAll('[title]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        // Simple tooltip initialization - Bootstrap will handle it
        (tooltipTriggerEl as any).setAttribute('data-bs-toggle', 'tooltip');
      });
    }, 100);
  }

  checkForDuplicateMatches(): void {
    const entityIdCounts = new Map<string, number[]>();

    this.matchResults.forEach((match, index) => {
      if (match.matched_entity_id) {
        if (!entityIdCounts.has(match.matched_entity_id)) {
          entityIdCounts.set(match.matched_entity_id, []);
        }
        entityIdCounts.get(match.matched_entity_id)!.push(index);
      }
    });

    entityIdCounts.forEach((indices, entityId) => {
      if (indices.length > 1) {
        indices.forEach(index => {
          this.matchResults[index].isDuplicate = true;
        });
      }
    });
  }

  clearFile(): void {
    this.selectedFile = null;
    this.uploadData = [];
    this.matchResults = [];
    this.currentStep = 'upload';
  }

  downloadTemplate(): void {
    // Create Excel template directly
    const templateData = [
      ['Company Name', 'ISIN'],
      ['Apple Inc.', 'US0378331005'],
      ['Microsoft Corporation', 'US5949181045']
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio Template');

    XLSX.writeFile(workbook, 'portfolio_template.xlsx');
  }

  goBack(): void {
    if (this.currentStep === 'review') {

      ConfirmationDialogComponent.open(this.dialog, {
        title: 'Confirm Action',
        message: 'All your selected matches will be lost. Are you sure you want to go back?',
        confirmText: 'Yes',
        cancelText: 'No',
        confirmColor: 'warn'
      }).subscribe(confirmed => {
        if (confirmed) {
          // User clicked confirm
          this.currentStep = 'upload';
        }
      });

    }
  }

  toggleSelection(match: MatchResult): void {
    // Only allow selecting valid, non-existing, non-duplicate public companies
    if (match.match_quality !== 'no_match' && !match.isExisting && match.gics_subindustry && (match.company_type === 'Public Company' || match.company_type === 'Public Investment Firm')) {
      if (match.selected) {
        match.selected = false;
      } else {
        // Check for duplicates before selecting
        const isDuplicate = this.matchResults.some(m =>
          m !== match && m.selected && m.matched_entity_id === match.matched_entity_id
        );
        if (!isDuplicate) {
          match.selected = true;
        }
      }
    }
  }

  toggleAllHighQuality(): void {
    const selectableMatches = this.highQualityMatches.filter(m => !m.isExisting && m.gics_subindustry && (m.company_type === 'Public Company' || m.company_type === 'Public Investment Firm'));
    const allSelected = selectableMatches.every(m => m.selected);

    selectableMatches.forEach(match => {
      match.selected = !allSelected;
    });
  }

  getSelectedCount(): number {
    const selectedMatches = this.matchResults.filter(m =>
      m.selected &&
      m.match_quality !== 'no_match' &&
      !m.isExisting &&
      m.gics_subindustry &&
      (m.company_type === 'Public Company' || m.company_type === 'Public Investment Firm')
    );
    const uniqueEntityIds = new Set(selectedMatches.map(m => m.matched_entity_id).filter(id => id));
    return uniqueEntityIds.size;
  }

  // Removed unused count helper methods after simplifying the UI

  get highQualityMatches(): MatchResult[] {
    return this.matchResults.filter(m => m.match_quality === 'high').sort((a, b) => {
      // Duplicates first
      if (a.isDuplicate && !b.isDuplicate) return -1;
      if (!a.isDuplicate && b.isDuplicate) return 1;

      // Then other issues (not already in watchlist)
      const aHasIssue = !a.gics_subindustry || (a.company_type !== 'Public Company' && a.company_type !== 'Public Investment Firm');
      const bHasIssue = !b.gics_subindustry || (b.company_type !== 'Public Company' && b.company_type !== 'Public Investment Firm');

      if (aHasIssue && !bHasIssue) return -1;
      if (!aHasIssue && bHasIssue) return 1;
      return 0;
    });
  }

  get lowQualityMatches(): MatchResult[] {
    return this.matchResults.filter(m => m.match_quality === 'low' || m.match_quality === 'no_match').sort((a, b) => {
      // Duplicates first
      if (a.isDuplicate && !b.isDuplicate) return -1;
      if (!a.isDuplicate && b.isDuplicate) return 1;

      // Then other issues (not already in watchlist)
      const aHasIssue = !a.gics_subindustry || (a.company_type !== 'Public Company' && a.company_type !== 'Public Investment Firm');
      const bHasIssue = !b.gics_subindustry || (b.company_type !== 'Public Company' && b.company_type !== 'Public Investment Firm');

      if (aHasIssue && !bHasIssue) return -1;
      if (!aHasIssue && bHasIssue) return 1;
      return 0;
    });
  }

  get allHighQualitySelected(): boolean {
    const selectableMatches = this.highQualityMatches.filter(m => !m.isExisting && m.gics_subindustry && (m.company_type === 'Public Company' || m.company_type === 'Public Investment Firm'));
    return selectableMatches.length > 0 && selectableMatches.every(m => m.selected);
  }

  hasIssue(match: MatchResult): boolean {
    return !match.gics_subindustry || (match.company_type !== 'Public Company' && match.company_type !== 'Public Investment Firm');
  }

  // Search and sort properties
  searchText = '';
  sortBy = 'score'; // 'score', 'name', 'isin'
  sortOrder = 'desc'; // 'asc', 'desc'

  // Icon filters
  showDuplicates = true;
  showIssues = true;
  showExisting = true;
  showManual = true;

  // Filter and sort matches
  getFilteredMatches(matches: MatchResult[]): MatchResult[] {
    let filtered = matches;

    // Search filter
    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      filtered = matches.filter(m =>
        m.input_data?.name?.toLowerCase().includes(search) ||
        m.input_data?.isin?.toLowerCase().includes(search) ||
        m.matched_entity_name?.toLowerCase().includes(search) ||
        m.matched_isin?.toLowerCase().includes(search)
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      // Manual matches first
      if (a.isManual && !b.isManual) return -1;
      if (!a.isManual && b.isManual) return 1;

      // Then by selected sort criteria
      let aVal, bVal;
      switch (this.sortBy) {
        case 'score':
          aVal = a.match_score || 0;
          bVal = b.match_score || 0;
          break;
        case 'name':
          aVal = a.matched_entity_name || a.input_data?.name || '';
          bVal = b.matched_entity_name || b.input_data?.name || '';
          break;
        case 'isin':
          aVal = a.matched_isin || a.input_data?.isin || '';
          bVal = b.matched_isin || b.input_data?.isin || '';
          break;
        default: return 0;
      }

      if (aVal < bVal) return this.sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Simple header click sorting
  toggleSort(by: 'score' | 'name' | 'isin'): void {
    if (this.sortBy === by) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = by;
      this.sortOrder = by === 'score' ? 'desc' : 'asc';
    }
  }

  get filteredHighQualityMatches(): MatchResult[] {
    return this.getFilteredMatches(this.highQualityMatches);
  }

  get filteredLowQualityMatches(): MatchResult[] {
    return this.getFilteredMatches(this.lowQualityMatches);
  }

  checkExistingCompanies(): void {
    this.matchResults.forEach(match => {
      const isExisting = this.existingCompanies.some(existing =>
        existing.entityid === match.matched_entity_id
      );
      if (isExisting) {
        match.selected = false;
        match.isExisting = true;
      }
    });
  }

  // Removed unused style helper

  createWatchlist(): void {
    if (this.getSelectedCount() === 0) {
      this.snackBar.open('Please select at least one company', 'Close', { duration: 3000 });
      return;
    }

    const totalAfterAdd = this.existingCount + this.getSelectedCount();
    if (totalAfterAdd > this.maxCompanies) {
      this.snackBar.open(`Cannot add ${this.getSelectedCount()} companies. Watchlist would exceed ${this.maxCompanies} limit (currently ${this.existingCount} companies)`, 'Close', { duration: 5000 });
      return;
    }

    const selectedMatches = this.matchResults.filter(m => m.selected);
    const uniqueCompanies = new Map();

    selectedMatches.forEach(match => {
      if (match.matched_entity_id && !uniqueCompanies.has(match.matched_entity_id)) {
        uniqueCompanies.set(match.matched_entity_id, {
          entityid: match.matched_entity_id,
          entity_name: match.matched_entity_name,
          entity_gics: match.gics_subindustry,
          isin: match.matched_isin
        });
      }
    });

    this.dialogRef.close({ companies: Array.from(uniqueCompanies.values()) });
  }

  setActiveTab(tab: 'matched' | 'partial'): void {
    this.activeTab = tab;
  }

  editMatch(match: MatchResult): void {
    const dialogRef = this.dialog.open(CompanySearchPopupComponent, {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: {
        mode: 'edit',
        editMatch: match,
        watchlist: this.data.watchlist,
        benchmark: this.data.benchmark,
        isBenchmark: this.isBenchmark
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.selectedCompany) {
        const wasInPartial = match.match_quality !== 'high';
        const wasExisting = match.isExisting;

        // Update the match with the selected company
        match.matched_entity_id = result.selectedCompany.entityid;
        match.matched_entity_name = result.selectedCompany.entity_name;
        match.matched_isin = result.selectedCompany.isin;
        match.gics_subindustry = result.selectedCompany.gsubind_extended || result.selectedCompany.gsubind;
        match.company_type = result.selectedCompany.companytype;
        match.match_score = 100; // Manual selection gets 100% score
        match.match_quality = 'high';
        match.isManual = true; // Mark as manually edited
        match.selected = true;

        // Reset existing status and re-check
        match.isExisting = false;
        this.checkExistingCompanies();

        // Show appropriate message based on what happened
        let message = `✓ ${match.input_data?.name || 'Company'} edited successfully`;
        if (wasInPartial) {
          message = `✓ ${match.input_data?.name || 'Company'} moved to Matched Companies`;
        } else if (wasExisting) {
          message = `✓ ${match.input_data?.name || 'Company'} replaced with new company`;
        }

        this.snackBar.open(message, 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      }
    });
  }

  onCancel(): void {
    //confirm if the user wants to cancel
    if (this.currentStep === 'review') {

      ConfirmationDialogComponent.open(this.dialog, {
        title: 'Confirm Action',
        message: 'All your selected matches will be lost. Are you sure you want to cancel?',
        confirmText: 'Yes',
        cancelText: 'No',
        confirmColor: 'warn'
      }).subscribe(confirmed => {
        if (confirmed) {
          // User clicked confirm
          this.dialogRef.close();
        }
      });

    }else{
      this.dialogRef.close();
    }

  }

  startProgressAnimation(): void {
    const totalCompanies = this.uploadData.length;
    const incompleteData = this.uploadData.filter(company => !company.isin || !company.name).length;
    const completenessFactor = 1 + (incompleteData / totalCompanies) * 2; // 1x to 3x slower for incomplete data

    const increment = Math.max(1, Math.min(3, 100 / (totalCompanies / 15))); // Slower increments
    const interval = Math.max(300, Math.min(1200, totalCompanies * completenessFactor * 3)); // Longer intervals for incomplete data

    this.progressInterval = setInterval(() => {
      if (this.progressValue < 95) {
        this.progressValue += Math.random() * increment;
        if (this.progressValue > 95) this.progressValue = 95;
      }
    }, interval);
  }

  stopProgressAnimation(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }
}